import plugin from 'tailwindcss/plugin'
import defaultTheme from 'tailwindcss/defaultTheme'
import presetV2 from './tailwind.preset.dsv2.js'

export default {
  darkMode: 'class', // Enable dark mode via class strategy
  content: [
    './app/components/**/*.{js,vue,ts,jsx,tsx}',
    './app/layouts/**/*.{vue,tsx,jsx}',
    './app/pages/**/*.{vue,tsx,jsx}',
    './app/**/*.{vue,tsx,jsx}',
    './app/plugins/**/*.{js,ts}',
    './nuxt.config.{js,ts}'
  ],

  safelist: [
    'before:w-[1.5rem]',
    'before:h-[1.5rem]',
    'before:w-[1.25rem]',
    'before:h-[1.25rem]',
    'min-h-[17.0625rem]',
    'max-h-[17.0625rem]',
    'border-t-alert-error-stroke',
    'bg-semantic-surface-container-variant-low',
    'bg-semantic-vertical-finance-base',
    '!max-w-[10rem]',
    'lg:!max-w-[20rem]',
    // HI-1288 Medicare step redesign — step6 grid placement comes from wrapperClass strings
    // in app/constants/application/health-insurance-v2 (not covered by `content` globs).
    'lg:col-span-2',
    'lg:col-start-1',
    'lg:col-start-2',
    'lg:row-start-1',
    'lg:row-start-2',
    'lg:row-start-3',
    'lg:row-start-4',
    'lg:row-start-5',
    'font-normal',
    // CLUB-839 — the NMI line in MSAT address options is built as an HTML string in
    // app/utils/energy/msat.js (not covered by `content` globs).
    'block',
    'whitespace-nowrap',
    'text-semantic-sizing-body-sm',
    'text-semantic-surface-on-surface-variant',
    // HI-1430 Energy SQPP — `maxWidthClass` on the usage row comes from
    // app/constants/funnel/energy-sqpp.js, which the `content` globs above do
    // not scan (they cover app/**/*.vue|tsx|jsx, not app/constants/**/*.js).
    // Without this the class lands on the element and computes to `max-width:
    // none`, silently leaving the rows full-width. `max-w-[22rem]`, used by the
    // other rows in that file, is already generated from a scanned template.
    'max-w-[34rem]'
  ],

  plugins: [
    plugin(function ({ addVariant, matchUtilities, theme, addUtilities }) {
      addVariant('not-last', '&:not(:last-child)')

      addVariant('last', '&:last-child')

      matchUtilities(
        {
          'exact-w': value => ({
            'min-width': value,
            width: value,
            'max-width': value
          })
        },
        { values: theme('width') }
      )

      matchUtilities(
        {
          'exact-h': value => ({
            'min-height': value,
            height: value,
            'max-height': value
          })
        },
        { values: theme('height') }
      )

      matchUtilities(
        {
          wh: value => ({
            width: value,
            height: value
          })
        },
        { values: theme('width') }
      )

      matchUtilities(
        {
          'exact-wh': value => ({
            'min-width': value,
            'max-width': value,
            width: value,
            'min-height': value,
            'max-height': value,
            height: value
          })
        },
        { values: theme('width') }
      )

      // Safezone utilities for device safe area insets
      addUtilities({
        '.safezone': {
          paddingTop: 'env(safe-area-inset-top, 16px)',
          paddingRight: 'env(safe-area-inset-right, 16px)',
          paddingBottom: 'env(safe-area-inset-bottom, 16px)',
          paddingLeft: 'env(safe-area-inset-left, 16px)'
        },
        '.safezone-x': {
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)'
        },
        '.safezone-y': {
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)'
        },
        '.pt-safe': {
          paddingTop: 'env(safe-area-inset-top)'
        },
        '.pb-safe': {
          paddingBottom: 'env(safe-area-inset-bottom)'
        }
      })

      /**
       * Font size utility sizes responsive
       * based on figma (https://www.figma.com/design/N7b9wTfpbFSwSuJgUEOxPX/%F0%9F%9F%A3-Compare-Club---2025-Design-System-Rebrand?m=auto&node-id=2718-7100&t=qXUsGjJKsJ2oVBcH-1)
       */
      addUtilities({
        // hero
        '.text-semantic-sizing-hero': {
          fontSize: '2.6rem', // 60
          lineHeight: '100%',

          '@media (min-width: 768px)': {
            fontSize: '3.75rem', // 96
            lineHeight: '100%'
          }
        },

        // display
        '.text-semantic-sizing-display-lg': {
          fontSize: '3rem', // 48
          lineHeight: '120%',

          '@media (min-width: 768px)': {
            fontSize: '4.5rem', // 72
            lineHeight: '120%'
          }
        },
        '.text-semantic-sizing-display': {
          fontSize: '2.25rem', // 36
          lineHeight: '120%',

          '@media (min-width: 768px)': {
            fontSize: '3.75rem', // 60
            lineHeight: '120%'
          }
        },
        '.text-semantic-sizing-display-sm': {
          fontSize: '1.875rem', // 30
          lineHeight: '120%',

          '@media (min-width: 768px)': {
            fontSize: '3rem', // 48
            lineHeight: '120%'
          }
        },

        // headline
        '.text-semantic-sizing-headline-lg': {
          fontSize: '1.875rem', // 30
          lineHeight: '120%',

          '@media (min-width: 768px)': {
            fontSize: '2.25rem', // 36
            lineHeight: '120%'
          }
        },
        '.text-semantic-sizing-headline': {
          fontSize: '1.25rem', // 20
          lineHeight: '120%',

          '@media (min-width: 768px)': {
            fontSize: '1.875rem', // 30
            lineHeight: '120%'
          }
        },
        '.text-semantic-sizing-headline-sm': {
          fontSize: '1.125rem', // 18
          lineHeight: '120%',

          '@media (min-width: 768px)': {
            fontSize: '1.5rem', // 24
            lineHeight: '120%'
          }
        },

        // body
        '.text-semantic-sizing-body-lg': {
          fontSize: '1.125rem', // 18
          lineHeight: '150%',

          '@media (min-width: 768px)': {
            fontSize: '1.25rem', // 20
            lineHeight: '150%'
          }
        },
        '.text-semantic-sizing-body': {
          lineHeight: '150%',

          '@media (min-width: 768px)': {
            fontSize: '1.125rem', // 18
            lineHeight: '150%'
          }
        },
        '.text-semantic-sizing-body-sm': {
          fontSize: '0.875rem', // 14
          lineHeight: '150%',

          '@media (min-width: 768px)': {
            fontSize: '1rem', // 16
            lineHeight: '150%'
          }
        },

        // label
        '.text-semantic-sizing-label-lg': {
          fontSize: '0.75rem', // 12
          lineHeight: '120%',

          '@media (min-width: 768px)': {
            fontSize: '0.875rem', // 14
            lineHeight: '120%'
          }
        },
        '.text-semantic-sizing-label': {
          fontSize: '0.625rem', // 10
          lineHeight: '120%',

          '@media (min-width: 768px)': {
            fontSize: '0.75rem', // 12
            lineHeight: '120%'
          }
        },
        '.text-semantic-sizing-label-sm': {
          fontSize: '0.5rem', // 8
          lineHeight: '120%',

          '@media (min-width: 768px)': {
            fontSize: '0.625rem', // 10
            lineHeight: '120%'
          }
        },

        // button
        '.text-base-font-size-lg': {
          fontSize: '1.125rem', // 18
          lineHeight: '100%' // button 100% | atom 150%
        },
        '.text-base-font-size-base': {
          lineHeight: '100%' // button 100% | atom 150%
        },
        '.text-base-font-size-sm': {
          fontSize: '0.875rem', // 14
          lineHeight: '150%'
        },
        '.text-base-font-size-xs': {
          fontSize: '0.75rem', // 12
          lineHeight: '150%'
        }
      })

      /**
       * Justify content and items safe utilities
       */
      addUtilities({
        // justify content safe
        '.justify-start-safe': {
          justifyContent: 'safe flex-start'
        },
        '.justify-center-safe': {
          justifyContent: 'safe center'
        },
        '.justify-end-safe': {
          justifyContent: 'safe flex-end'
        },

        // justify items safe
        '.justify-items-start-safe': {
          justifyItems: 'safe start'
        },
        '.justify-items-center-safe': {
          justifyItems: 'safe center'
        },
        '.justify-items-end-safe': {
          justifyItems: 'safe end'
        },

        // align items safe
        '.items-start-safe': {
          alignItems: 'safe start'
        },
        '.items-center-safe': {
          alignItems: 'safe center'
        },
        '.items-end-safe': {
          alignItems: 'safe end'
        }
      }),

      addUtilities({
        '.absolute-x-center': {
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)'
        },
        '.absolute-y-center': {
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)'
        }
      })

      addUtilities({
        '.break-word': {
          wordBreak: 'break-word'
        }
      })
    })
  ],
  presets: [presetV2],
  theme: {
    screens: {
      xxxs: '320px',
      xxs: '375px',
      xs: '414px',
      '2xs': '480px',
      '3xs': '540px',
      '1.5lg': '1100px',
      '1.5xl': '1410px',
      '1.6xl': '1440px',
      xxl: '2000px',
      ...defaultTheme.screens
    },

    extend: {
      colors: {
        primary: '#0ac16a',
        dark: '#193560',
        black: '#010812',
        danger: '#ea5455',
        success: '#28c76f',
        'transparent-dark': 'rgba(25,53,96,.75)',
        alert: {
          error: {
            stroke: '#c50e0e',
            fill: '#ffe1e1'
          },
          info: {
            stroke: '#74ceff',
            fill: '#eff9ff'
          },
          warning: {
            stroke: '#ffe847',
            fill: '#fffce7'
          },
          success: {
            stroke: '#BDD883',
            fill: '#f2fff9'
          }
        },
        gray: {
          5: '#f8f8f8', // form field bg:dark
          10: '#f9f9f9', // light bg1
          20: '#E2E4E6',
          30: '#bebebe', // grey optional
          40: '#aaaaaa', // inactive
          80: '#666766', // disclaimer

          25: '#f5f8fa',
          75: '#86878d', // label text:default
          125: '#7f7f7f',
          200: '#f5f7f6',
          210: '#CCCCCC',
          250: '#C0C0C0',
          300: '#F2F2F2',
          350: '#f0f0f0', // light bg2
          400: '#d2d2d2', // outlines divider
          500: '#828282',
          600: '#DDDDDD'
        },
        green: {
          5: '#f3fcf8',
          10: '#e7f9f0',
          20: '#cef3e1',
          30: '#b6ecd2',
          40: '#9de6c3',
          50: '#84e0b4',
          60: '#6cdaa6',
          70: '#54d497',
          80: '#3bcd88',
          90: '#23c779',
          100: '#2CD18F'
        },
        blue: {
          5: '#f4f5f7', // #f5f6f7
          10: '#e8eBef', // '#e7eaee',
          15: '#F4F7FC',
          20: '#d1d7df',
          30: '#bac2cf',
          40: '#a3aebf',
          50: '#8c9aaf',
          60: '#7586a0',
          70: '#5e7290',
          80: '#475d80',
          90: '#304970',
          500: '#1183C5', // blue crayola
          600: '#1a8b9d',
          800: '#362092', // blue picotee
          900: '#0b1d39', // footer bg
          fb: '#395185',
          'linked-in': '#0a66c2'
        },
        zinc: {
          400: '#BEBEBE'
        },
        pink: {
          600: '#e83f6f'
        },
        neutral: {
          50: '#fafafa',
          75: '#fafbfc',
          100: '#f9f9f9',
          400: '#86878d'
        },
        sky: {
          700: '#435f7d'
        },
        yellow: {
          200: '#fdba12',
          600: '#ffb10a'
        },
        orange: {
          650: '#ED6A5A',
          700: '#FF8642',
          750: '#FF86421A',
          800: '#E3A013' // orange
        },
        cyan: {
          600: '#01baef'
        },
        purple: {
          70: '#552692', // purple
          700: '#5526920D', // purple
          80: '#381960', // promo bg
          90: '#180c33', // dark purple,
          100: '#932189' // dark purple
        },
        brown: {
          50: '#9A5533'
        }
      },
      backgroundPosition: {
        'left-1': '0.125rem 0.35rem'
      },
      spacing: {
        1.5: '0.45rem',
        7.5: '1.875rem',
        15: '3.75rem',
        17: '4.5625rem',
        18: '4.6875rem'
      },
      boxShadow: {
        'md-invert': '0 -0.25rem 1rem rgba(0, 0, 0, 0.12)',
        md: '0 0.5rem 1rem rgba(0, 0, 0, 0.12)',
        lg: '0 0 0.4375rem rgba(14, 31, 53, 0.12)',
        xl: '0 0.75rem 3.125rem 0 rgba(25, 53, 96, 0.3)',
        inner: 'inset 0 0.25rem 0.5rem rgba(0, 0, 0, 0.16)',
        default: '0 0.3125rem 3.125rem 0.3125rem rgba(25, 53, 96, 0.06)',
        bordered: '0 .25rem .5rem rgba(14,31,53,.16)',

        panel: '0 0.3125rem 2.5rem rgba(0, 0, 0, 0.12)',
        onclick: 'inset 0 0.25rem 0.5rem rgba(0, 0, 0, 0.16)',
        hover: '0 3.5rem 10rem 0.3125rem rgba(0, 0, 0, 0.05)',
        header: '0 0.125rem 0.125rem rgba(14, 31, 53, 0.12)',
        'header-inset': 'inset 0 0.125rem 0.125rem rgba(14, 31, 53, 0.12)',
        'header-inset-light':
          'inset 0 0.0625rem 0.125rem rgba(14, 31, 53, 0.06)',

        'floating-sm': '0 0.1875rem 0.5rem rgba(25, 53, 96, 0.32)',
        'floating-lg': '0 3.125rem 6.25rem rgba(25, 53, 96, 0.2)',

        'right-only': '0.3125rem 0 0.875rem -0.25rem rgb(0 0 0 / 10%)',
        'bottom-only': '0 0.6875rem 0.625rem -0.5rem rgb(0 0 0 / 10%)',

        'sm-1': '0 0 0.125rem 0 rgba(14, 31, 53, 0.12)',
        'sm-2': '0 0.125rem 0.25rem rgba(14, 31, 53, 0.12)',
        'sm-3': '0 0.1875rem 0.4375rem rgba(14, 31, 53, 0.12)',
        'sm-4': '0 0.25rem 0.5rem rgba(14, 31, 53, 0.16)',
        'sm-5': '0 0.4375rem 0.75rem rgba(14, 31, 53, 0.14)',

        'md-1': '0 0.375rem 1.25rem rgba(0, 0, 0, 0.12)',
        'md-2': '0 0.875rem 1.5rem rgba(14, 31, 53, 0.16)',
        'md-3': '0 1rem 1.75rem -0.0625rem rgba(14, 31, 53, 0.18)',
        'md-4': '0 1.75rem 2.5rem -0.0625rem rgba(14, 31, 53, 0.2)',

        'lg-1': '0 2rem 3rem -0.125rem rgba(14, 31, 53, 0.2)',
        'lg-2': '0 2.25rem 3.5rem -0.25rem rgba(14, 31, 53, 0.28)',
        'lg-3': '0 2.5rem 4rem -0.375rem rgba(14, 31, 53, 0.36)',
        'lg-4': '0 3rem 5.25rem -0.5rem rgba(14, 31, 53, 0.48)',

        'author-hero-card': '0 1.875rem 3.75rem -2rem rgba(30, 12, 57, 0.45)',
        'author-image': '0 14px 28px -14px rgba(30, 12, 57, 0.35)'
      },
      dropShadow: {
        'sm-1': '0 0 0.125rem rgba(14, 31, 53, 0.12)',
        'sm-2': '0 0.125rem 0.25rem rgba(14, 31, 53, 0.12)',
        'sm-3': '0 0.1875rem 0.4375rem rgba(14, 31, 53, 0.12)',
        'sm-4': '0 0.25rem 0.5rem rgba(14, 31, 53, 0.16)',

        'md-1': '0 0.375rem 1.25rem rgba(0, 0, 0, 0.12)',
        'md-2': '0 0.875rem 1.5rem rgba(14, 31, 53, 0.16)',
        'md-3': '0 1rem 1.75rem -0.0625rem rgba(14, 31, 53, 0.18)',
        'md-4': '0 1.75rem 2.5rem -0.0625rem rgba(14, 31, 53, 0.2)',

        'lg-1': '0 2rem 3rem -0.125rem rgba(14, 31, 53, 0.2)',
        'lg-2': '0 2.25rem 3.5rem -0.25rem rgba(14, 31, 53, 0.28)',
        'lg-3': '0 2.5rem 4rem -0.375rem rgba(14, 31, 53, 0.36)',
        'lg-4': '0 3rem 5.25rem -0.5rem rgba(14, 31, 53, 0.48)'
      },
      borderWidth: {
        1: '1px',
        3: '3px',
        6: '6px'
      },
      maxWidth: {
        initial: 'initial',
        unset: 'unset',
        'nav-container': '58.5625rem',
        'wrapper-xs': '38rem',
        'wrapper-xs2': '40.875rem',
        'wrapper-sm': '46rem', // 8cols | 736px
        'wrapper-sm2': '55.25rem',
        'wrapper-md': '58rem', // 10cols | 928px
        'wrapper-lg': '70rem', // 12cols | 1120px (default)
        'wrapper-lg-px-4': '72rem', // 12cols |  1152
        'wrapper-xl': '84rem',
        '1.5lg': '34rem',
        '1.5xl': '40rem',
        '2.5xl': '45.6875rem',
        '4.5xl': '62.725rem',
        '5.5xl': '68rem',
        '8xl': '90rem',
        '8xxl': '120rem',
        content: '100rem'
      },
      width: {
        unset: 'unset',
        'wrapper-xs': '38rem',
        'wrapper-xs2': '40.875rem',
        'wrapper-sm': '46rem',
        'wrapper-sm2': '55.25rem',
        'wrapper-md': '58.125rem',
        'wrapper-lg': '70rem', // default
        'wrapper-lg-px-4': '72rem',
        'wrapper-xl': '84rem',
        inherit: 'inherit',
        26: '6.4969rem',
        66: '16.3125rem',
        76: '19.75rem',
        100: '25.875rem',
        120: '47rem'
      },
      fontSize: {
        '2xxs': '0.5rem',
        '2xs': '0.625rem',
        '1.5xl': ['1.375rem', '1.875rem'],
        '2.5xl': ['1.75rem', '2.25rem'],
        '3.5xl': ['2rem', '2.5rem'],
        '4.5xl': ['2.5rem', '3rem'],
        '6.5xl': '4rem',
        '5xl': ['3rem', '3.5rem']
      },
      zIndex: {
        60: '60'
      },
      borderRadius: {
        '2md': '0.5rem', // 8px
        lg: '10px',
        '2.5xl': '20px'
      },
      minWidth: {
        unset: 'unset',
        initial: 'initial',
        inherit: 'inherit',
        200: '200px',
        70: '17.5rem',
        80: '20rem'
      },
      maxHeight: {
        initial: 'initial',
        unset: 'unset',
        inherit: 'inherit',
        44: '44px',
        214: '214px',
        300: '300px'
      },
      minHeight: {
        initial: 'initial',
        unset: 'unset',
        inherit: 'inherit'
      },
      height: {
        auto: 'auto',
        unset: 'unset',
        inherit: 'inherit',
        15: '60px',
        17: '73px',
        '4xl': '28.5625rem'
      },
      lineHeight: {
        6.5: '1.625rem',
        12: '3rem'
      },
      gridTemplateColumns: {
        'contentful-3column-pc':
          'minmax(1rem,1fr) minmax(auto,19.3125rem) minmax(auto,40.875rem) minmax(auto,19.3125rem) minmax(1rem,1fr)',
        'contentful-3column-mb':
          'minmax(1rem,1fr) minmax(auto, 19.3125rem) 1fr minmax(auto, 19.3125rem) minmax(1rem,1fr)',
        'contentful-sidebar-v2':
          'minmax(1rem,1fr) minmax(auto, 46rem) minmax(auto,24rem) minmax(1rem,1fr)',
        'contentful-sidebar':
          'minmax(1rem,1fr) minmax(auto,42.5375rem) minmax(auto,20.1875rem) minmax(1rem,1fr)',
        'contentful-sidebar-light':
          'minmax(1rem,1fr) minmax(auto,53.25rem) minmax(auto,18.75rem) minmax(1rem,1fr)',
        'contentful-light':
          'minmax(1rem,1fr) minmax(auto,32rem) minmax(auto,32rem) minmax(1rem,1fr)'
      }
    }
  }
}
